import { Box, Chip, Divider, Tooltip, Typography } from '@mui/material';
import { FC } from 'react';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { TOrderDetailsRowChildProps } from './types';

const DutyRow: FC<{ label: string; value: number | null; currency: string }> = ({
	label,
	value,
	currency,
}) => (
	<Box
		sx={{
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'space-between',
			ml: 4,
		}}
	>
		<Typography variant="body2" sx={{ fontSize: '.8rem' }} color="text.secondary">
			{label}
		</Typography>
		<AmountAndCurrency amount={value ?? 0} currency={currency} size={0.8} />
	</Box>
);

export const OrderDetailsRowChild: FC<TOrderDetailsRowChildProps> = ({
	child,
	paymentCurrency,
	detalization,
}) => {
	return (
		<Box>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					gap: 2,
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
					<Typography variant="body2">
						Product: <span style={{ fontWeight: 'bold' }}>{child.id_provided_by_em} </span>
					</Typography>
					{child.location_warehouse_country && (
						<Tooltip title="Warehouse Country">
							<Chip
								size="small"
								sx={{ p: '0 5px', ml: 1, fontSize: '.7rem', height: '20px' }}
								label={
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
										<CountryWithFlag countryCode={child.location_warehouse_country} size="s" />
									</Box>
								}
							/>
						</Tooltip>
					)}
				</Box>

				{detalization ? (
					<Box sx={{ display: 'flex', gap: 1, alignSelf: 'end' }}>
						<Typography variant="body2" sx={{ fontSize: '.8rem' }} color="text.secondary">
							<AmountAndCurrency
								amount={child?.cost_provided_by_em ?? 0}
								currency={paymentCurrency}
								size={0.8}
							/>
							&nbsp;&times;&nbsp;{child.quantity}&nbsp;=&nbsp;
						</Typography>
						<AmountAndCurrency
							amount={(child?.cost_provided_by_em ?? 0) * (child?.quantity ?? 0)}
							currency={paymentCurrency}
							bold="bold"
							size={0.9}
						/>
					</Box>
				) : (
					<Box>
						<AmountAndCurrency
							amount={child?.total_item_cost ?? 0}
							currency={paymentCurrency}
							bold="bold"
							size={1}
						/>
					</Box>
				)}
			</Box>
			{detalization && (
				<>
					{!!child?.customs_duty && (
						<DutyRow
							label={`Customs Duty (${child.custom_duty_rate}%)`}
							value={child.customs_duty}
							currency={paymentCurrency}
						/>
					)}
					<DutyRow
						label={`Item VAT (${child.vat_rate}%)`}
						value={child.item_only_vat}
						currency={paymentCurrency}
					/>
					{!!child.prorated_delivery_cost && (
						<DutyRow
							label="Delivery Charge"
							value={child.prorated_delivery_cost}
							currency={paymentCurrency}
						/>
					)}
					{!!child.item_delivery_charge_vat && (
						<DutyRow
							label={`VAT on Delivery Charge (${child.vat_rate}%)`}
							value={child.item_delivery_charge_vat}
							currency={paymentCurrency}
						/>
					)}
					{!!child.prorated_eas_fee_vat && (
						<DutyRow
							label="VAT on EAS Fee (22%)"
							value={child.prorated_eas_fee_vat}
							currency={paymentCurrency}
						/>
					)}

					<Divider sx={{ mt: 0.4, mb: 0.4, ml: 4 }} />
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
							ml: 4,
						}}
					>
						<Typography variant="body2" sx={{ fontSize: '.8rem', fontWeight: 'bold' }}>
							Total
						</Typography>
						<AmountAndCurrency
							amount={child.total_item_cost ?? 0}
							currency={paymentCurrency}
							bold="bold"
							size={0.9}
						/>
					</Box>
				</>
			)}
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'start',
					alignItems: 'start',
					gap: 0,
					ml: 4,
					mb: 1,
				}}
			>
				<Typography variant="body2" sx={{ fontSize: '.8rem' }}>
					<span>Product Description: </span>
					<span style={{ fontWeight: 'bold' }}>{child.description}</span>
				</Typography>
			</Box>

			{!!child.customs_description && (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'start',
						alignItems: 'start',
						gap: 0,
						ml: 4,
						mb: 1,
					}}
				>
					<Typography variant="body2" sx={{ fontSize: '.8rem' }}>
						Customs Description:
						<span style={{ fontWeight: 'bold' }}> {child.customs_description ?? '—'}</span>
					</Typography>
				</Box>
			)}

			<Box sx={{ ml: 4, mb: 1 }}>
				<Typography variant="body2" sx={{ fontSize: '.8rem' }}>
					Weight (kg):
					<span style={{ fontWeight: 'bold' }}>
						{' '}
						{child.weight_in_kg != null ? child.weight_in_kg : '—'}
					</span>
				</Typography>
			</Box>

			<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, ml: 4 }}>
				<Box sx={{ width: '200px' }}>
					<Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
						<Typography variant="body2" sx={{ fontSize: '.8rem' }}>
							HS Code:
						</Typography>
						<Typography variant="h5" sx={{ fontSize: '.9rem', fontWeight: 'bold' }}>
							{child.hs_code || '—'}
						</Typography>
					</Box>
				</Box>
				<Box sx={{ width: '100%' }}>
					<Typography variant="body2" color="text.secondary" sx={{ p: 0 }}>
						{child.hs_description}
					</Typography>
				</Box>
			</Box>
		</Box>
	);
};
